import { useCallback, useEffect, useRef, useState } from "react";

import useLatestScribeNote from "features/medical-scribe/hooks/use-latest-scribe-note.hook";
import {
  AMBIENT_SCRIBE_NOTE_PARENT_TYPE,
  AMBIENT_SCRIBE_NOTE_STATUS
} from "services/types/ambient-scribe-note.type";

type SoapProvenanceParams = {
  scribeId: number | undefined;
  enabled: boolean;
  isGeneratingSoap: boolean;
  soapNotes: string | null | undefined;
};

/**
 * The run behind the Scribe tab's SOAP text: its prompt, and whether it failed. The text itself
 * still comes from `remote.soap_notes`; this reads the newest matching row in
 * `ai_ambientscribe_notes`.
 *
 * Deliberately NOT scoped by `parentId`. The backend stamps a SOAP run with
 * `parent_id = scribe.chart_note_id` (the recording's own linked note, NULL when it has none), not
 * the note whichever screen opened this dialog — and Insert Scribe lets the user pick any
 * recording, so the two routinely differ. Scoping would return zero rows for most picks. It also
 * does not need to: `soap_notes` is scribe-level and any run rewrites it, so the newest ChartNote
 * row IS the run behind what is on screen.
 */
export default function useSoapProvenance({
  scribeId,
  enabled,
  isGeneratingSoap,
  soapNotes
}: SoapProvenanceParams) {
  const {
    newest: chartNoteNote,
    prompt,
    refetch
  } = useLatestScribeNote({
    scribeId,
    parentType: AMBIENT_SCRIBE_NOTE_PARENT_TYPE.CHART_NOTE,
    enabled,
    withPrompt: true
  });

  // A regeneration writes a NEW note row, and `/async/` deliberately does not invalidate
  // `AmbientScribeNotes` (no row exists at dispatch time). Same trick as `AudiologySummaryTab`:
  // the progress flag going true → false on the existing poll is the signal that one now does.
  // Safe to refetch on that edge — the backend commits the row inside `_generate_ai_content`'s
  // `try`, while the flag is reset in its `finally`, so the row is always there first.
  const wasGeneratingSoapRef = useRef(isGeneratingSoap);
  useEffect(() => {
    if (wasGeneratingSoapRef.current && !isGeneratingSoap) refetch();
    wasGeneratingSoapRef.current = isGeneratingSoap;
  }, [isGeneratingSoap, refetch]);

  // A SOAP generation that FAILED is otherwise invisible: its completion notification carries no
  // status and `is_generating_soap` just returns to `false`, so the Scribe tab showed "No Scribe
  // available yet" and invited the user to retry the same failing call.
  //
  // Gated on `ranSoapThisSession` once content exists, and that guard is load-bearing — do not
  // collapse it into a bare `status === FAILED` the way `AudiologySummaryTab` can. There, the
  // content and the failure come from the same query. Here the content is `remote.soap_notes`,
  // which the notes table does not govern: `record_note` is best-effort and gated by a backend
  // setting, so a SUCCESSFUL run can leave no row at all and an older failure stays `newest`
  // forever. Unguarded, the banner would sit permanently over content newer than the failure it
  // warns about.
  //
  // With no content there is nothing to contradict, so the failure always shows (the backend
  // wipes `soap_notes` before generating, so a failure leaves the field empty).
  const [ranSoapThisSession, setRanSoapThisSession] = useState(false);
  const soapFailure =
    chartNoteNote?.status === AMBIENT_SCRIBE_NOTE_STATUS.FAILED &&
    (!soapNotes || ranSoapThisSession)
      ? chartNoteNote
      : undefined;

  // Never reset: it means "an Apply happened while this was mounted", which is what licenses the
  // failure banner over existing content. The details are keyed by scribe id in Insert Scribe, so
  // it resets per scribe there on its own.
  const markSoapRun = useCallback(() => setRanSoapThisSession(true), []);

  return { prompt, soapFailure, markSoapRun };
}
